



























import Vue from 'vue';
import { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options';
import { mapActions, mapGetters } from 'vuex';
import { TranslateResult } from 'vue-i18n';
import { PhoneConfirmRequestGetSuccess } from '@/requests/mainApi';
import { IResponse } from '@/requests/requestMachine';
import AuthFormContainer from '@/components/modules/Auth/AuthFormContainer.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiLabel from '@/components/ui/UiLabel.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiCheckbox from '@/components/ui/UiCheckbox.vue';
import { masks } from '@/utils/plugins/inputMask';
import { removePhoneMask, getRecaptchaSiteKey, getRecaptchaIgnoreKey } from '@/utils/helpers/helpFunctions';
import { mainSiteLink } from '@/utils/plugins/links';
import { VueRecaptcha } from 'vue-recaptcha';

interface Data {
  localPhone: string;
  errorMessage: TranslateResult;
  recaptchaToken: string | null;
  siteKey: string;
}

interface Methods {
  validatePhone: () => boolean;
  getPhoneConfirmRequest: (variables: any) => Promise<IResponse<PhoneConfirmRequestGetSuccess>>;
  verifyCaptcha: (token: string) => void;
  isCaptchaExpired: () => void;
  getOrganization: () => void;
}

interface Computed {
  labelText: TranslateResult;
}

interface Props {
  phone: string;
  recaptcha: any;
}

export default Vue.extend({
  name: 'SignInPhoneStep',
  components: {
    AuthFormContainer,
    UiInput,
    UiLabel,
    UiButton,
    UiCheckbox,
    VueRecaptcha,
  },
  props: {
    phone: {
      type: String,
      default: () => '',
    },
    recaptcha: VueRecaptcha,
  },
  data() {
    return {
      masks,
      mainSiteLink,
      localPhone: this.phone,
      errorMessage: '',
      recaptchaToken: null,
      recaptchaSiteKey: getRecaptchaSiteKey(),
    };
  },
  computed: {
    labelText() {
      return this.errorMessage || this.$t('auth.signIn.phoneStep.phoneNumber');
    },
    ...mapGetters(['organization']),
  },
  mounted() {
    this.getOrganization();
  },
  methods: {
    getFullLink(route: string) {
      return mainSiteLink + route;
    },
    validatePhone(): boolean {
      if (!this.localPhone) {
        this.errorMessage = this.$t('auth.signIn.phoneStep.errors.phoneIsEmpty');
        return false;
      }
      if (removePhoneMask(this.localPhone).length !== 11) {
        this.errorMessage = this.$t('auth.signIn.phoneStep.errors.phoneNotValid');
        return false;
      }
      this.errorMessage = '';
      return true;
    },
    verifyCaptcha(token: string): void {
      this.recaptchaToken = token;
    },
    isCaptchaExpired() {
      this.recaptchaToken = null;
    },
	
    async requestPhoneConfirm(): Promise<void> {
      if (!this.validatePhone() || !this.recaptchaToken) {
        return;
      }

      const isAdmin = location.href.includes('admin-');

      try {
        const response = await this.getPhoneConfirmRequest({
          phone: this.localPhone,
          isAdmin,
          gRecaptchaResponse: this.recaptchaToken,
          ignoreCaptchaKey: getRecaptchaIgnoreKey()
        });

        if (response.code === 400) {
          this.errorMessage = this.$t('auth.signIn.phoneStep.errors.phoneNotExist');
          return;
        }

        if (response.code === 429) {
          this.errorMessage = this.$t('auth.signIn.phoneConfirmStep.errors.tooFrequentRequestes');
          return;
        }

        if (response.code === 431) {
          this.$emit('problemStep');
          return;
        }

        // 403 - риелтор или заблокированный пользователь: показываем причину, а не общий экран проблемы
        if (response.code === 403 && response.errorMessage) {
          this.errorMessage = response.errorMessage;
          return;
        }

        if (!response.status && response.code !== 200) {
          this.$emit('problemStep');
          return;
        }

        const { status, phoneCall, token } = response.body;

        if (status !== 'confirmed' && status !== 'send') {
          return;
        }

        this.$emit('phoneConfirmRequestSend', this.localPhone, phoneCall, token);
      } catch (e) {
        console.error(e);
      }
    },
	
    ...mapActions(['getPhoneConfirmRequest', 'getOrganization']),
  },
} as ThisTypedComponentOptionsWithRecordProps<Vue, Data, Methods, Computed, Props>);
